import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";

interface InstallationItem {
  itemId: string;
  productId: string;
  serialNumber: string;
  qrCode: string;
  barcodeData: string;
  description: string;
  installationStatus: "pending" | "in_progress" | "completed" | "failed" | "rework_required";
  installationDate?: string;
  installationTime?: string;
  installedBy: string;
  location: {
    building?: string;
    floor?: string;
    room?: string;
    coordinates?: string;
    gpsLocation?: {
      latitude: number;
      longitude: number;
      accuracy: number;
    };
  };
  photos: {
    beforeInstall?: string[];
    duringInstall?: string[];
    afterInstall?: string[];
  };
  qualityCheck: {
    passed: boolean;
    checklistItems: {
      itemId: string;
      description: string;
      checked: boolean;
      notes?: string;
    }[];
    defects: {
      defectId: string;
      description: string;
      severity: "minor" | "major" | "critical";
      photo?: string;
      resolutionRequired: boolean;
    }[];
  };
  notes: string;
}

interface InstallationTrackingData {
  jobId: string;
  workOrderNumber: string;
  installationDate: string;
  installationTeam: string[];
  jobSiteInfo: {
    siteName: string;
    address: string;
    contactPerson: string;
    contactPhone: string;
    accessInstructions: string;
  };
  items: InstallationItem[];
  overallStatus: "not_started" | "in_progress" | "completed" | "delayed" | "cancelled";
  progressPercentage: number;
  startTime?: string;
  endTime?: string;
  totalHours?: number;
  weatherConditions?: string;
  safetyNotes: string;
  customerSignature?: {
    signatureData: string;
    customerName: string;
    signatureDate: string;
  };
  completionNotes: string;
}

class InstallationTrackingWizardStore {
  rootStore: RootStore;

  // UI State
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;
  scanningMode = false;
  selectedItemIndex: number | null = null;

  // Form Data
  formData: InstallationTrackingData = {
    jobId: "",
    workOrderNumber: "",
    installationDate: new Date().toISOString().split("T")[0],
    installationTeam: [],
    jobSiteInfo: {
      siteName: "",
      address: "",
      contactPerson: "",
      contactPhone: "",
      accessInstructions: "",
    },
    items: [],
    overallStatus: "not_started",
    progressPercentage: 0,
    safetyNotes: "",
    completionNotes: "",
  };

  // Lookup Data
  jobs: any[] = [];
  products: any[] = [];
  qualityCheckTemplates: any[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    this.formData.workOrderNumber = this.generateWorkOrderNumber();
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  };

  updateFormData = <K extends keyof InstallationTrackingData>(
    field: K,
    value: InstallationTrackingData[K]
  ) => {
    this.formData[field] = value;
    if (field === "items") {
      this.calculateProgress();
    }
  };

  addInstallationItem = (item: InstallationItem) => {
    this.formData.items.push(item);
    this.calculateProgress();
  };

  removeInstallationItem = (index: number) => {
    this.formData.items.splice(index, 1);
    this.calculateProgress();
  };

  updateInstallationItem = (index: number, item: InstallationItem) => {
    this.formData.items[index] = item;
    this.calculateProgress();
  };

  addTeamMember = (userId: string) => {
    if (!this.formData.installationTeam.includes(userId)) {
      this.formData.installationTeam.push(userId);
    }
  };

  removeTeamMember = (userId: string) => {
    const index = this.formData.installationTeam.indexOf(userId);
    if (index > -1) {
      this.formData.installationTeam.splice(index, 1);
    }
  };

  setScanningMode = (enabled: boolean) => {
    this.scanningMode = enabled;
  };

  setSelectedItemIndex = (index: number | null) => {
    this.selectedItemIndex = index;
  };

  scanBarcode = async (barcodeData: string) => {
    try {
      // Simulate barcode scanning and product lookup
      const product = this.products.find(p =>
        p.barcode === barcodeData || p.serialNumber === barcodeData
      );

      if (product) {
        const newItem: InstallationItem = {
          itemId: this.generateItemId(),
          productId: product.id,
          serialNumber: product.serialNumber || barcodeData,
          qrCode: barcodeData,
          barcodeData,
          description: product.description || product.name,
          installationStatus: "pending",
          installedBy: "",
          location: {},
          photos: {},
          qualityCheck: {
            passed: false,
            checklistItems: [],
            defects: [],
          },
          notes: "",
        };

        this.addInstallationItem(newItem);
        this.success = `Product scanned: ${product.name}`;
      } else {
        this.error = "Product not found in database";
      }
    } catch (error: any) {
      this.error = error.message || "Failed to scan barcode";
    }
  };

  generateWorkOrderNumber = (): string => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, "0");
    return `WO-${year}${month}${day}-${random}`;
  };

  generateItemId = (): string => {
    return `ITEM-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  };

  calculateProgress = () => {
    if (this.formData.items.length === 0) {
      this.formData.progressPercentage = 0;
      this.formData.overallStatus = "not_started";
      return;
    }

    const completedItems = this.formData.items.filter(
      item => item.installationStatus === "completed"
    ).length;

    const inProgressItems = this.formData.items.filter(
      item => item.installationStatus === "in_progress"
    ).length;

    this.formData.progressPercentage = Math.round(
      (completedItems / this.formData.items.length) * 100
    );

    if (completedItems === this.formData.items.length) {
      this.formData.overallStatus = "completed";
    } else if (inProgressItems > 0 || completedItems > 0) {
      this.formData.overallStatus = "in_progress";
    } else {
      this.formData.overallStatus = "not_started";
    }
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.error = null;

    switch (this.activeStep) {
      case 0:
        if (!this.formData.jobId) {
          this.error = "Please select a job/project";
          return false;
        }
        if (!this.formData.jobSiteInfo.siteName) {
          this.error = "Please enter the job site name";
          return false;
        }
        if (!this.formData.jobSiteInfo.address) {
          this.error = "Please enter the job site address";
          return false;
        }
        if (this.formData.installationTeam.length === 0) {
          this.error = "Please assign at least one team member";
          return false;
        }
        return true;

      case 1:
        if (this.formData.items.length === 0) {
          this.error = "Please scan or add at least one installation item";
          return false;
        }

        const invalidItems = this.formData.items.some(
          item => !item.productId || !item.description
        );
        if (invalidItems) {
          this.error = "Please complete all item information";
          return false;
        }
        return true;

      case 2:
        const uninstalledItems = this.formData.items.filter(
          item => item.installationStatus === "pending"
        );
        if (uninstalledItems.length > 0) {
          this.error = `${uninstalledItems.length} items are still pending installation`;
          return false;
        }

        const failedQualityChecks = this.formData.items.filter(
          item => item.installationStatus === "completed" && !item.qualityCheck.passed
        );
        if (failedQualityChecks.length > 0) {
          this.error = "All completed items must pass quality checks";
          return false;
        }
        return true;

      case 3:
        if (!this.formData.completionNotes) {
          this.error = "Please provide completion notes";
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  // API Actions
  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      // Load jobs
      if (this.rootStore.jobStore) {
        await this.rootStore.jobStore.find({
          query: {
            $limit: 100,
            status: "active",
          },
        });
      }

      // Load products
      if (this.rootStore.productsStore) {
        await this.rootStore.productsStore.find({
          query: {
            $limit: 500,
            active: true,
          },
        });
      }

      runInAction(() => {
        this.jobs = this.rootStore.jobStore?.entities || [];
        this.products = this.rootStore.productsStore?.entities || [];

        // Mock quality check templates (would come from API)
        this.qualityCheckTemplates = [
          {
            id: "1",
            name: "Standard Installation Check",
            items: [
              { id: "1", description: "Item properly secured", required: true },
              { id: "2", description: "All connections tight", required: true },
              { id: "3", description: "Level and plumb", required: true },
              { id: "4", description: "No visible damage", required: true },
              { id: "5", description: "Meets specifications", required: true },
            ],
          },
          {
            id: "2",
            name: "Electrical Installation Check",
            items: [
              { id: "1", description: "Proper grounding", required: true },
              { id: "2", description: "Correct voltage", required: true },
              { id: "3", description: "No exposed wiring", required: true },
              { id: "4", description: "Circuit protection in place", required: true },
            ],
          },
        ];

        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load initial data";
        this.loading = false;
      });
    }
  };

  submitInstallationTracking = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      // Calculate total hours if start and end times are available
      if (this.formData.startTime && this.formData.endTime) {
        const start = new Date(`${this.formData.installationDate}T${this.formData.startTime}`);
        const end = new Date(`${this.formData.installationDate}T${this.formData.endTime}`);
        this.formData.totalHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      }

      // Create installation tracking record
      const trackingData = {
        ...this.formData,
        submittedAt: new Date().toISOString(),
        submittedBy: this.rootStore.authStore?.user?.id,
      };

      // API call would go here
      await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulated API call

      runInAction(() => {
        this.success = "Installation tracking submitted successfully!";
        this.loading = false;

        // Reset form after successful submission
        setTimeout(() => {
          this.reset();
          this.rootStore.routerStore.goTo("Dashboard");
        }, 2000);
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to submit installation tracking";
        this.loading = false;
      });
    }
  };

  // Computed Values
  get users() {
    return this.rootStore.userStore?.entities || [];
  }

  get selectedJob() {
    return this.jobs.find((j) => j.id === this.formData.jobId);
  }

  get completedItems() {
    return this.formData.items.filter(item => item.installationStatus === "completed");
  }

  get pendingItems() {
    return this.formData.items.filter(item => item.installationStatus === "pending");
  }

  get failedItems() {
    return this.formData.items.filter(item => item.installationStatus === "failed");
  }

  get qualityIssues() {
    return this.formData.items.flatMap(item =>
      item.qualityCheck.defects.filter(defect => defect.severity === "critical" || defect.severity === "major")
    );
  }

  get teamMembers() {
    return this.formData.installationTeam.map(memberId =>
      this.users.find(user => user.id === memberId)
    ).filter(Boolean);
  }

  get getProductById() {
    return (productId: string) => this.products.find((p) => p.id === productId);
  }

  get isInstallationComplete() {
    return this.formData.items.length > 0 &&
           this.formData.items.every(item => item.installationStatus === "completed");
  }

  // Reset
  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.scanningMode = false;
    this.selectedItemIndex = null;
    this.formData = {
      jobId: "",
      workOrderNumber: this.generateWorkOrderNumber(),
      installationDate: new Date().toISOString().split("T")[0],
      installationTeam: [],
      jobSiteInfo: {
        siteName: "",
        address: "",
        contactPerson: "",
        contactPhone: "",
        accessInstructions: "",
      },
      items: [],
      overallStatus: "not_started",
      progressPercentage: 0,
      safetyNotes: "",
      completionNotes: "",
    };
  };
}

export default InstallationTrackingWizardStore;